import {
  IAggregateTypes,
  IColumnTypes,
  IColumnValuesCount,
  ISpreadsheetData,
  IViewPortColumn
} from 'components/spreadsheet/types'
import {
  numberWithCommas,
  calculatePercentage,
  formatPercentage,
  neatRound
} from 'components/spreadsheet/helpers/format'
import { DATE_COLUMN_TYPES, NUMBER_COLUMN_TYPES, SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { ICellValue, ITableRow } from 'types'
import { isValidValue, getOptionsSetForColumn } from 'components/spreadsheet/helpers/validation'
import { getRowValue, rowDataArrayToObject } from 'components/spreadsheet/helpers/functions'
const EMPTY_RETURN = 'n/a'
export const calculateAggregate = (
  rows: ICellValue[],
  aggregate: number,
  totalRowsInView: number,
  column: IViewPortColumn,
  rowsData: {
    [columnId: string]: ICellValue
  }[],
  columnValuesCount: IColumnValuesCount
) => {
  switch (aggregate) {
    case IAggregateTypes['# Rows']:
      return numberWithCommas(rows.length)
    case IAggregateTypes['% Total Rows']:
      return formatPercentage((rows.length / totalRowsInView) * 100, 2)
    case IAggregateTypes.Empty:
      return numberWithCommas(calculateEmptyAggregate(rows))
    case IAggregateTypes.Filled:
      return numberWithCommas(calculateFilledAggregate(rows))
    case IAggregateTypes.Unique:
      return numberWithCommas(calculateUniqueAggregate(rows))
    case IAggregateTypes['% Empty']: {
      const pctEmpty = calculatePctEmptyAggregate(rows)
      if (isNaN(pctEmpty)) return EMPTY_RETURN
      else return formatPercentage(pctEmpty)
    }
    case IAggregateTypes['% Filled']: {
      const pctFilled = calculatePctFilledAggregate(rows)
      if (isNaN(pctFilled)) return EMPTY_RETURN
      else return formatPercentage(pctFilled)
    }
    case IAggregateTypes['% Unique']: {
      const pctUnique = calculatePctUniqueAggregate(rows)
      if (isNaN(pctUnique)) return EMPTY_RETURN
      else return formatPercentage(pctUnique)
    }
    case IAggregateTypes.Sum:
      return numberWithCommas(calculateSumAggregate(rows as number[]))
    case IAggregateTypes.Average: {
      const avg = calculateAverageAggregate(rows)
      if (isNaN(avg)) return EMPTY_RETURN
      else return numberWithCommas(avg)
    }
    case IAggregateTypes.Min: {
      const min = calculateMinAggregate(rows as number[])
      if (min !== undefined && isFinite(min)) return numberWithCommas(min)
      else return EMPTY_RETURN
    }
    case IAggregateTypes.Max: {
      const max = calculateMaxAggregate(rows as number[])
      if (max !== undefined && isFinite(max)) return numberWithCommas(max)
      else return EMPTY_RETURN
    }
    case IAggregateTypes.Range: {
      const range = calculateRangeAggregate(rows as number[])
      if (range !== undefined && isFinite(range)) return numberWithCommas(range)
      else return EMPTY_RETURN
    }
    case IAggregateTypes['Earliest Date']: {
      const minDate = calculateEarliestDateAggregate(rows)
      if (minDate !== undefined) return minDate
      else return EMPTY_RETURN
    }
    case IAggregateTypes['Latest Date']: {
      const maxDate = calculateLatestDateAggregate(rows)
      if (maxDate !== undefined) return maxDate
      else return EMPTY_RETURN
    }
    case IAggregateTypes['Date Range (Days)']: {
      const dateRange = calculateDateRangeAggregate(rows)
      if (dateRange !== undefined) return numberWithCommas(Math.round(dateRange / (1000 * 60 * 60 * 24)))
      else return EMPTY_RETURN
    }
    case IAggregateTypes['# Issues']:
      return numberWithCommas(calculateIssuesAggregate(rows, column, rowsData, columnValuesCount))
    case IAggregateTypes['# Valid']:
      return numberWithCommas(calculateValidValuesAggregate(rows, column, rowsData, columnValuesCount))
    case IAggregateTypes['% Issues']: {
      const pctIssues = calculatePctIssuesAggregate(rows, column, rowsData, columnValuesCount)
      if (isNaN(pctIssues)) return EMPTY_RETURN
      else return formatPercentage(pctIssues)
    }
    case IAggregateTypes['% Valid']: {
      const pctValid = calculatePctValidValuesAggregate(rows, column, rowsData, columnValuesCount)
      if (isNaN(pctValid)) return EMPTY_RETURN
      else return formatPercentage(pctValid)
    }
    default:
      return ''
  }
}

export const isValidAggregateTypeForColType = (colType: IColumnTypes, aggregateType: number): boolean => {
  const allAggregateTypes = [
    IAggregateTypes.None,
    IAggregateTypes['# Rows'],
    IAggregateTypes.Empty,
    IAggregateTypes.Filled,
    IAggregateTypes['% Total Rows'],
    IAggregateTypes['% Empty'],
    IAggregateTypes['% Filled'],
    IAggregateTypes.Unique,
    IAggregateTypes['% Unique'],
    IAggregateTypes['# Issues'],
    IAggregateTypes['# Valid'],
    IAggregateTypes['% Issues'],
    IAggregateTypes['% Valid']
  ]

  const numberAggregateTypes = [
    IAggregateTypes.Sum,
    IAggregateTypes.Average,
    IAggregateTypes.Min,
    IAggregateTypes.Max,
    IAggregateTypes.Range
  ].concat(allAggregateTypes)

  const dateAggregateTypes = [
    IAggregateTypes['Earliest Date'],
    IAggregateTypes['Latest Date'],
    IAggregateTypes['Date Range (Days)']
  ].concat(allAggregateTypes)

  if (allAggregateTypes.indexOf(aggregateType) !== -1) {
    return true
  } else if (numberAggregateTypes.indexOf(aggregateType) !== -1) return NUMBER_COLUMN_TYPES.includes(colType)
  else if (dateAggregateTypes.indexOf(aggregateType) !== -1) return DATE_COLUMN_TYPES.includes(colType)
  else return false
}

export const calculateEmptyAggregate = (rows: ICellValue[]) => {
  return rows.filter(
    (cell: ICellValue) => cell === undefined || cell === null || (Array.isArray(cell) && cell.length === 0)
  ).length
}

export const calculateFilledAggregate = (rows: ICellValue[]) => {
  return rows.filter(
    (cell: ICellValue) =>
      cell !== undefined && cell !== null && (!Array.isArray(cell) || (Array.isArray(cell) && cell.length > 0))
  ).length
}

export const calculateUniqueAggregate = (rows: ICellValue[]) => {
  return new Set(
    rows.filter(
      (cell: ICellValue) =>
        cell !== undefined && cell !== null && (!Array.isArray(cell) || (Array.isArray(cell) && cell.length > 0))
    )
  ).size
}

export const calculatePctEmptyAggregate = (rows: ICellValue[]) => {
  const empty = calculateEmptyAggregate(rows)
  return calculatePercentage(empty / rows.length)
}

export const calculatePctFilledAggregate = (rows: ICellValue[]) => {
  const filled = calculateFilledAggregate(rows)
  return calculatePercentage(filled / rows.length)
}

export const calculatePctUniqueAggregate = (rows: ICellValue[]) => {
  const unique = calculateUniqueAggregate(rows)
  return calculatePercentage(unique / rows.length)
}

export const calculateSumAggregate = (rows: number[]) => {
  return neatRound(rows.reduce((a: number, b: number) => a + b, 0))
}

export const calculateAverageAggregate = (rows: ICellValue[]) => {
  const sum = calculateSumAggregate(rows as number[])
  const total = rows.filter((cell: ICellValue) => cell !== undefined && cell !== null).length
  const average = sum / total
  return neatRound(average)
}

export const calculateMinAggregate = (rows: number[]) => {
  let len = rows.length
  let min = Infinity

  while (len--) {
    min = rows[len] < min ? rows[len] : min
  }
  return min
}

export const calculateMaxAggregate = (rows: number[]) => {
  let len = rows.length
  let max = -Infinity

  while (len--) {
    max = rows[len] > max ? rows[len] : max
  }
  return max
}

export const calculateRangeAggregate = (rows: number[]) => {
  const max = calculateMaxAggregate(rows)
  const min = calculateMinAggregate(rows)
  const range = max - min
  return neatRound(range)
}

export const calculateEarliestDateAggregate = (rows: ICellValue[]) => {
  let minValue

  for (let i = 0; i < rows.length; i++) {
    const columnValue = rows[i]
    if (
      columnValue !== undefined &&
      columnValue !== null &&
      typeof columnValue === 'string' &&
      Date.parse(columnValue) &&
      (minValue === undefined || columnValue < minValue)
    )
      minValue = columnValue
  }

  return minValue
}

export const calculateLatestDateAggregate = (rows: ICellValue[]) => {
  let maxValue

  for (let i = 0; i < rows.length; i++) {
    const columnValue = rows[i]
    if (
      columnValue !== undefined &&
      columnValue !== null &&
      typeof columnValue === 'string' &&
      Date.parse(columnValue) &&
      (maxValue === undefined || columnValue > maxValue)
    )
      maxValue = columnValue
  }

  return maxValue
}

export const calculateDateRangeAggregate = (rows: ICellValue[]) => {
  let minValue
  let maxValue

  for (let i = 0; i < rows.length; i++) {
    const columnValue = rows[i]
    if (
      columnValue !== undefined &&
      columnValue !== null &&
      typeof columnValue === 'string' &&
      Date.parse(columnValue) &&
      (maxValue === undefined || Date.parse(columnValue) > maxValue)
    )
      maxValue = Date.parse(columnValue)
    if (
      columnValue !== undefined &&
      columnValue !== null &&
      typeof columnValue === 'string' &&
      Date.parse(columnValue) &&
      (minValue === undefined || Date.parse(columnValue) < minValue)
    )
      minValue = Date.parse(columnValue)
  }
  if (minValue !== undefined && maxValue !== undefined) return maxValue - minValue
  else return undefined
}

export const calculateIssuesAggregate = (
  rows: ICellValue[],
  column: IViewPortColumn,
  rowsData: {
    [columnId: string]: ICellValue
  }[],
  columnValuesCount: IColumnValuesCount
) => {
  const numberOfValidValues = calculateValidValuesAggregate(rows, column, rowsData, columnValuesCount)
  const numberOfIssues = rows.length - numberOfValidValues

  return numberOfIssues
}

export const calculatePctIssuesAggregate = (
  rows: ICellValue[],
  column: IViewPortColumn,
  rowsData: {
    [columnId: string]: ICellValue
  }[],
  columnValuesCount: IColumnValuesCount
) => {
  const numberOfIssues = calculateIssuesAggregate(rows, column, rowsData, columnValuesCount)
  return calculatePercentage(numberOfIssues / rows.length)
}

export const calculateValidValuesAggregate = (
  rows: ICellValue[],
  column: IViewPortColumn,
  rowsData: {
    [columnId: string]: ICellValue
  }[],
  columnValuesCount: IColumnValuesCount
) => {
  const col = column.column
  const kind = col.kind
  const kindOptions = col.kindOptions
  const isSelectKind = SELECT_COLUMN_TYPES.includes(kind) && !col.isJoined
  const hasDeps = !!kindOptions?.tableOptions?.dependencies?.length

  // Fast path for select/multiselect with non-dependency cached options.
  // Reuses the column-keyed memoized Set from validation.ts — first call after
  // a column-options change pays the build cost; every subsequent aggregate
  // recompute *and* every per-cell isValidValue call returns the same Set
  // until cachedOptions / manualOptions reference changes.
  if (isSelectKind && !hasDeps) {
    const cached = kindOptions?.tableOptions?.cachedOptions as string[] | undefined
    const manual = kindOptions?.manualOptions
    const optionsSet = getOptionsSetForColumn(col, cached, manual)

    const noBlanks = !!col.validationNoBlanks
    const noDuplicates = !!col.validationNoDuplicates
    const seenForCol = noDuplicates ? columnValuesCount[col.publicId] : undefined
    const isMultiselect = kind === 'multiselect'

    let validCount = 0
    for (let i = 0; i < rows.length; i++) {
      const value = rows[i]

      if (
        noBlanks &&
        (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0))
      ) {
        continue
      }

      if (noDuplicates && seenForCol && value !== undefined && value !== null) {
        const seen = seenForCol[(value as { toString(): string }).toString()]
        if (seen !== undefined && seen > 1) continue
      }

      if (isMultiselect) {
        if (value && Array.isArray(value)) {
          let allMatch = true
          for (let j = 0; j < value.length; j++) {
            const v = value[j]
            if (!v) {
              allMatch = false
              break
            }
            const key = typeof v === 'string' ? v : (v as { toLocaleString(): string }).toLocaleString()
            if (!optionsSet.has(key)) {
              allMatch = false
              break
            }
          }
          if (!allMatch) continue
        }
      } else if (value && typeof value === 'string' && !optionsSet.has(value)) {
        continue
      }

      validCount++
    }

    return validCount
  }

  let validCount = 0
  for (let i = 0; i < rows.length; i++) {
    if (!isValidValue(rows[i], rowsData[i], columnValuesCount, col).error) validCount++
  }
  return validCount
}

export const calculatePctValidValuesAggregate = (
  rows: ICellValue[],
  column: IViewPortColumn,
  rowsData: {
    [columnId: string]: ICellValue
  }[],
  columnValuesCount: IColumnValuesCount
) => {
  const numberOfValidValues = calculateValidValuesAggregate(rows, column, rowsData, columnValuesCount)
  return calculatePercentage(numberOfValidValues / rows.length)
}

// Pre-compute all aggregate values once instead of passing rows to each Aggregate component
// This significantly reduces memory usage for large tables (avoids 140k rows × N columns references)
export const computeAggregateValues = (
  aggregateRows: ITableRow[],
  columns: IViewPortColumn[],
  spreadsheetData: ISpreadsheetData
): Record<string, string> => {
  if (spreadsheetData.loading || !aggregateRows) {
    return {}
  }

  // First, determine which columns actually need aggregates computed
  // Skip columns with aggregate === None (except column 0 which defaults to # Rows)
  const columnsNeedingAggregates = columns
    .map((viewportColumn, columnNumber) => {
      const column = viewportColumn.column
      const effectiveAggregate =
        columnNumber === 0 && (column.aggregate === undefined || column.aggregate === IAggregateTypes.None)
          ? IAggregateTypes['# Rows']
          : column.aggregate

      if (effectiveAggregate !== undefined && effectiveAggregate !== IAggregateTypes.None) {
        return { viewportColumn, columnNumber, effectiveAggregate }
      }
      return null
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  // If no columns need aggregates, return empty - don't create rowsData
  if (columnsNeedingAggregates.length === 0) {
    return {}
  }

  const results: Record<string, string> = {}

  // Only create rowsData if we actually need it (for validation-related aggregates)
  // Most aggregate types don't need rowsData, only # Issues, # Valid, % Issues, % Valid —
  // and even then, isValidValue only consults rowData when a select column has
  // dependency-grouped cached options. Skipping the 1-object-per-row allocation
  // for the common no-dependency case avoids 100k+ object allocations on large tables.
  const needsRowsData = columnsNeedingAggregates.some(({ effectiveAggregate, viewportColumn }) => {
    const isValidationAggregate =
      effectiveAggregate === IAggregateTypes['# Issues'] ||
      effectiveAggregate === IAggregateTypes['# Valid'] ||
      effectiveAggregate === IAggregateTypes['% Issues'] ||
      effectiveAggregate === IAggregateTypes['% Valid']
    if (!isValidationAggregate) return false
    return !!viewportColumn.column.kindOptions?.tableOptions?.dependencies?.length
  })
  // Convert array-based rowData to object format for validation aggregates
  const rowsData = needsRowsData
    ? aggregateRows.map((row) =>
        rowDataArrayToObject(row.rowData, spreadsheetData.indexToColumnId, row._rowIndex, spreadsheetData.columnStore)
      )
    : []

  columnsNeedingAggregates.forEach(({ viewportColumn, effectiveAggregate }) => {
    const column = viewportColumn.column
    // Count-only aggregates need no cell values; reading one per row would otherwise scan the
    // whole table on every render (the first column defaults to "# Rows").
    if (effectiveAggregate === IAggregateTypes['# Rows']) {
      results[column.publicId] = numberWithCommas(aggregateRows.length)
      return
    }
    if (effectiveAggregate === IAggregateTypes['% Total Rows']) {
      results[column.publicId] = formatPercentage((aggregateRows.length / spreadsheetData.totalRows) * 100, 2)
      return
    }
    let columnValues = aggregateRows.map((row) =>
      getRowValue(row, column.publicId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
    )

    if (column.kind === 'checkbox') {
      columnValues = columnValues.map((val) => (!val || val === 'false' ? null : val))
    }

    results[column.publicId] = calculateAggregate(
      columnValues,
      effectiveAggregate,
      spreadsheetData.totalRows,
      viewportColumn,
      rowsData,
      spreadsheetData.columnValuesCount
    )
  })

  return results
}
